import emojiData from './generated/emojis.json';

export type LocalizedEmoji = {
  id: string;
  name: string;
  keywords: string[];
  skins: { unified: string; native: string }[];
  version: number;
  emoticons?: string[] | null;
  localizedShortcode?: string;
};

export type LocalizedEmojiData = {
  categories: { id: string; emojis: string[] }[];
  aliases: Record<string, string>;
  emojis: Record<string, LocalizedEmoji>;
};

export type EmojiDataProp = {
  id: string;
  name: string;
  unified?: string;
  native?: string;
  colons?: string;
  emoticons?: string[];
  skin?: number | null;
};

// Enforcing that the imported emojiData from massDriver conforms to our expectations
// Should throw ts errors if the shape changes via local builds or CI
const rawEmojiData = emojiData satisfies {
  emojis: Array<LocalizedEmoji & { emoticons: string[] | null }>;
  emojiCategories: LocalizedEmojiData['categories'];
  emojiAliases: { alias: string; id: string }[];
};

export const baseEmojiData: LocalizedEmojiData = {
  categories: rawEmojiData.emojiCategories,
  aliases: Object.fromEntries(
    rawEmojiData.emojiAliases.map(({ alias, id }) => [alias, id])
  ),
  emojis: Object.fromEntries(
    rawEmojiData.emojis.map((emoji) => [emoji.id, emoji])
  ),
};

export const resolveEmoji = (
  id: string,
  data: LocalizedEmojiData = baseEmojiData
): LocalizedEmoji | undefined =>
  data.emojis[id] ?? data.emojis[data.aliases[id]];

export const toEmojiData = (emoji: LocalizedEmoji): EmojiDataProp => ({
  id: emoji.id,
  name: emoji.name,
  unified: emoji.skins[0]?.unified,
  native: emoji.skins[0]?.native,
  colons: `:${emoji.id}:`,
  emoticons: emoji.emoticons ?? [],
});

export const localizeRecentEmoji = (
  stored: EmojiDataProp,
  data: LocalizedEmojiData
): EmojiDataProp => {
  const current = resolveEmoji(stored.id, data);
  const hasChangedUnicode =
    stored.unified &&
    stored.unified.toLocaleLowerCase() !==
      current?.skins[0]?.unified?.toLocaleLowerCase();

  return {
    ...stored,
    name: hasChangedUnicode ? stored.name : current?.name ?? stored.name,
    unified: stored.unified ?? current?.skins[0]?.unified,
  };
};
